import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import MultiEmailSelect from "../../../components/ChangeSelect";
import communityService from "../../../services/communityService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GrGroup } from "react-icons/gr";

const NewCommunityPage = () => {
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [showSuccessPopup, setShowSuccessPopup] = useState(false); // Success popup state
  const navigate = useNavigate(); // Initialize navigate hook
  const [activeTab, setActiveTab] = useState("general"); // State to track active tab
  const [generalInfo, setGeneralInfo] = useState({
    communityName: "",
    adminEmail: [],
    description: "",
  });

  const [addressInfo, setAddressInfo] = useState({
    addressLine1: "",
    addressLine2: "",
    postcode: "",
    city: "",
    state: "",
    country: "",
  });

  const [errors, setErrors] = useState({
    general: [],
    address: [],
  });

  const [isFormValid, setIsFormValid] = useState(true); // Track form validity

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const handleGeneralInfoChange = (e) => {
    const { name, value } = e.target;
    setGeneralInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressInfoChange = (e) => {
    const { name, value } = e.target;
    setAddressInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdminEmailChange = (emails) => {
    setGeneralInfo((prev) => ({
      ...prev,
      adminEmail: emails, // Store selected emails properly
    }));
  };

  // Validation function for all fields
  const validateForm = () => {
    let generalErrors = [];
    let addressErrors = [];

    // General Info Validation
    if (!generalInfo.communityName) generalErrors.push("Community Name");
    if (!generalInfo.adminEmail || generalInfo.adminEmail.length === 0)
      generalErrors.push("Admin Email"); // Ensure it's not empty
    if (!generalInfo.description) generalErrors.push("Description");

    // Address Validation
    if (!addressInfo.addressLine1) addressErrors.push("Address Line 1");
    if (!addressInfo.addressLine2) addressErrors.push("Address Line 2");
    if (!addressInfo.postcode) addressErrors.push("Postcode");
    if (!addressInfo.city) addressErrors.push("City");
    if (!addressInfo.state) addressErrors.push("State");
    if (!addressInfo.country) addressErrors.push("Country");

    // Set error messages
    setErrors({
      general: generalErrors,
      address: addressErrors,
    });

    // Ensure Errors Are Logged
    if (generalErrors.length > 0 || addressErrors.length > 0) {
      console.warn("Validation errors:", { generalErrors, addressErrors });
    }

    return generalErrors.length === 0 && addressErrors.length === 0;
  };

  const handleCreateClick = async () => {
    console.log("Create button clicked");

    const isValid = validateForm();
    setIsFormValid(isValid);

    if (!isValid) {
      console.log("Validation errors:", errors);
      return;
    }

    setIsLoading(true); // Start loading state

    // Extract community data
    const communityData = {
      Name: generalInfo.communityName,
      Description: generalInfo.description,
      AddressLine1: addressInfo.addressLine1,
      AddressLine2: addressInfo.addressLine2,
      Postcode: addressInfo.postcode,
      City: addressInfo.city,
      State: addressInfo.state,
      Country: addressInfo.country,
    };

    // Ensure you pass an array of emails (assuming generalInfo.adminEmail is an array)
    const emails = generalInfo.adminEmail; // Adjust according to your data structure

    console.log("Sending request with data:", {
      ...communityData,
      Emails: emails,
    });

    try {
      // Use the new API method addCommunityWithEmails
      const result = await communityService.addCommunityWithEmails(
        communityData,
        emails
      );
      console.log("Community created successfully:", result);
      toast.success("🎉 Community created successfully!", { autoClose: 2000 });

      // Show success popup
      setShowSuccessPopup(true);

      // Reset loading state and redirect after a delay
      setTimeout(() => {
        setShowSuccessPopup(false);
        navigate("/communities");
      }, 2000);
    } catch (error) {
      console.error("Failed to create community:", error);
    } finally {
      setIsLoading(false); // Stop loading state
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        {/* Main Content */}
        <div className="p-8 flex flex-col">
          {/* Header Section */}
          <div className="bg-white shadow p-4 sm:p-6 rounded-lg">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Left Side: Icon + Title */}
              <div className="flex items-center gap-4">
                <div className="bg-teal-100 p-3 sm:p-4 rounded-full">
                  <GrGroup className="w-10 h-10 sm:w-16 sm:h-16 text-teal-700" />
                </div>
                <h1 className="text-base sm:text-lg font-semibold text-[#111827]">
                  New Community
                </h1>
              </div>

              {/* Right Side: Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                <Button
                  variant={"hollowGreen"}
                  onClick={() => navigate("/communities")}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateClick}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? "Creating..." : "Create"}
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="flex">
            {/* General Info Tab */}
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>

            {/* Address Info Tab */}
            <button
              onClick={() => handleTabSwitch("address")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "address"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              Address Info
            </button>
          </div>

          {/* Form Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6 mt-0 bg-white shadow rounded-r-lg rounded-b-lg border-gray-300">
            {activeTab === "general" ? (
              <>
                {/* Community Name */}
                <div>
                  <Label>Community Name</Label>
                  <Input
                    type="text"
                    name="communityName"
                    value={generalInfo.communityName}
                    onChange={handleGeneralInfoChange}
                    placeholder="Enter Community Name"
                  />
                  {errors.general.includes("Community Name") && (
                    <p className="text-red-500 text-xs">
                      Community Name is required
                    </p>
                  )}
                </div>

                {/* Admin Email Dropdown */}
                <div>
                  <Label>Admin Email</Label>
                  <MultiEmailSelect
                    selectedEmails={generalInfo.adminEmail} // Ensure it reflects current state
                    onChange={handleAdminEmailChange} // Ensure changes update the state
                  />

                  {errors.general.includes("Admin Email") && (
                    <p className="text-red-500 text-xs">
                      Admin Email is required
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label>Description</Label>
                  <Input
                    type="text"
                    name="description"
                    value={generalInfo.description}
                    onChange={handleGeneralInfoChange}
                    placeholder="Enter Description"
                  />
                  {errors.general.includes("Description") && (
                    <p className="text-red-500 text-xs">
                      Description is required
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Address Line 1 */}
                <div>
                  <Label>Address Line 1</Label>
                  <Input
                    type="text"
                    name="addressLine1"
                    value={addressInfo.addressLine1}
                    onChange={handleAddressInfoChange}
                    placeholder="Enter Address Line 1"
                  />
                  {errors.address.includes("Address Line 1") && (
                    <p className="text-red-500 text-xs">
                      Address Line 1 is required
                    </p>
                  )}
                </div>

                {/* Address Line 2 */}
                <div>
                  <Label>Address Line 2</Label>
                  <Input
                    type="text"
                    name="addressLine2"
                    value={addressInfo.addressLine2}
                    onChange={handleAddressInfoChange}
                    placeholder="Enter Address Line 2"
                  />
                  {errors.address.includes("Address Line 2") && (
                    <p className="text-red-500 text-xs">
                      Address Line 2 is required
                    </p>
                  )}
                </div>

                {/* Postcode */}
                <div>
                  <Label>Postcode</Label>
                  <Input
                    type="text"
                    name="postcode"
                    value={addressInfo.postcode}
                    onChange={handleAddressInfoChange}
                    placeholder="Enter Postcode"
                  />
                  {errors.address.includes("Postcode") && (
                    <p className="text-red-500 text-xs">Postcode is required</p>
                  )}
                </div>

                {/* City */}
                <div>
                  <Label>City</Label>
                  <Input
                    type="text"
                    name="city"
                    value={addressInfo.city}
                    onChange={handleAddressInfoChange}
                    placeholder="Enter City"
                  />
                  {errors.address.includes("City") && (
                    <p className="text-red-500 text-xs">City is required</p>
                  )}
                </div>

                {/* State */}
                <div>
                  <Label>State</Label>
                  <Input
                    type="text"
                    name="state"
                    value={addressInfo.state}
                    onChange={handleAddressInfoChange}
                    placeholder="Enter State"
                  />
                  {errors.address.includes("State") && (
                    <p className="text-red-500 text-xs">State is required</p>
                  )}
                </div>

                {/* Country */}
                <div>
                  <Label>Country</Label>
                  <Input
                    type="text"
                    name="country"
                    value={addressInfo.country}
                    onChange={handleAddressInfoChange}
                    placeholder="Enter Country"
                  />
                  {errors.address.includes("Country") && (
                    <p className="text-red-500 text-xs">Country is required</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCommunityPage;
